/**
 * The Stock overview table.
 *
 * Two shapes, one set of columns. Grouped is the default: sizes sit under a collapsible
 * family header carrying the family's colour band, its name and its totals, which is what
 * turns forty one rows into fourteen things to look at. Flat drops the headers and the
 * subtotals and lists every size in one table, which is what you want when you are hunting
 * for a SKU rather than reading a category.
 *
 * The grouping is presentational in both cases: one `DataTable` per family or one for the
 * lot, so the responsive rule from DESIGN.md section 5 still applies and the whole thing
 * becomes a card list under 768px without a second code path. The columns, the sort, the
 * cells and the row drawer are the same objects either way, which is the only reason a
 * second mode is affordable at all.
 *
 * Every column declares an explicit width, so a product name never truncates before a number
 * does. Sorting reorders the families by their total for the sorted column and the sizes
 * inside each family by the same column; in flat mode the same comparator runs once over
 * every row.
 *
 * The collapse state and the chosen mode are remembered in localStorage per browser, because
 * the operator who always ignores Reconstitution should not have to close it every morning.
 */
import { useMemo } from 'react';
import type { StockPageConfig, StockPageRow } from '@elutide/shared';
import type { Column, DataTableSort } from '@/components';
import {
  Button,
  ChevronIcon,
  DataTable,
  DefinitionPopover,
  EmptyState,
  MoneyText,
} from '@/components';
import { useLocalPreference } from '@/lib/hooks';
import { formatCount } from '@/lib/format';
import { FAMILY_BAND } from './constants';
import { BestPriceCell, CostCell, PriceCell, ProductCell, QtyCell, SalesCell } from './cells';
import { COLUMN_LEGEND_ID, definitionFor, type LegendIndex } from './legend';
import {
  familyHasMoney,
  pageHasMoney,
  rowHasMoney,
  type AnyStockPageFamily,
  type AnyStockPageRow,
} from './types';

export const COLLAPSED_FAMILIES_KEY = 'stock.overview.collapsedFamilies';
export const GROUPING_KEY = 'stock.overview.grouping';

export const DEFAULT_SORT: DataTableSort = { by: 'product', dir: 'asc' };

/** Grouped by family, or one row per size. Grouped is the default and the fallback. */
export type StockGrouping = 'grouped' | 'flat';

export const DEFAULT_GROUPING: StockGrouping = 'flat';

/**
 * A stored preference that is not one of the two values is not a preference.
 *
 * localStorage holds whatever the last version of this code wrote, plus whatever anyone with
 * a devtools console felt like putting there. Anything unrecognised falls back to grouped
 * rather than rendering a third mode that does not exist.
 */
export function normalizeGrouping(value: unknown): StockGrouping {
  return value === 'flat' || value === 'grouped' ? value : DEFAULT_GROUPING;
}

export interface StockTableProps {
  families: readonly AnyStockPageFamily[];
  grouping: StockGrouping;
  config: StockPageConfig;
  legend: LegendIndex;
  sort: DataTableSort;
  onSortChange: (sort: DataTableSort) => void;
  canEditQty: boolean;
  canEditPrice: boolean;
  canSetVisible: boolean;
  onCommitQty: (input: { row: AnyStockPageRow; expectedQty: number; qty: number }) => Promise<void>;
  onCommitPrice: (input: { row: StockPageRow; priceCents: number }) => Promise<void>;
  onSetVisible: (row: AnyStockPageRow, isVisible: boolean) => void;
  onOpenDetail: (row: AnyStockPageRow, section?: 'quotes') => void;
  /** The Cost cell asks for the breakdown behind the number it is showing. */
  onOpenWac: (row: StockPageRow) => void;
  loading: boolean;
}

/**
 * How each sortable column ranks a size and a family.
 *
 * `days left` sorts ascending with the most urgent first, and a size with no sales sorts
 * last rather than first: no sales is not an emergency, it is an absence.
 */
const NO_SALES_RANK = Number.MAX_SAFE_INTEGER;

function rowRank(row: AnyStockPageRow, by: string): number {
  switch (by) {
    case 'on_hand':
      return row.on_hand;
    case 'sales_30d':
      return row.days_left ?? NO_SALES_RANK;
    // A money sort on a packer's page (a remembered preference, say) has nothing to rank
    // by, so every row ties and the name order below decides. Never NaN.
    case 'price':
      return rowHasMoney(row) ? row.price_cents : NO_SALES_RANK;
    case 'inv_value':
      return rowHasMoney(row) ? row.inv_value_cents : NO_SALES_RANK;
    case 'cost':
      return rowHasMoney(row) ? (row.wac_cents ?? NO_SALES_RANK) : NO_SALES_RANK;
    case 'best_price':
      return rowHasMoney(row) ? (row.best_price_cents ?? NO_SALES_RANK) : NO_SALES_RANK;
    case 'product':
    default:
      return 0;
  }
}

function familyRank(family: AnyStockPageFamily, by: string): number {
  switch (by) {
    case 'on_hand':
      return family.on_hand;
    case 'sales_30d':
      return family.worst_days_left ?? NO_SALES_RANK;
    case 'inv_value':
      return familyHasMoney(family) ? family.inv_value_cents : NO_SALES_RANK;
    case 'cost':
      return familyHasMoney(family) ? family.inventory_at_cost_cents : NO_SALES_RANK;
    case 'price':
    case 'best_price':
      // No family total exists for a per size price, so the family takes its worst size.
      return Math.min(...family.rows.map((row) => rowRank(row, by)), NO_SALES_RANK);
    case 'product':
    default:
      return 0;
  }
}

function compare(a: number, b: number, dir: 'asc' | 'desc'): number {
  return dir === 'asc' ? a - b : b - a;
}

/**
 * The leading number in a size label, for ordering sizes inside a product.
 *
 * "10mg" before "30mg" before "80mg", which `localeCompare` gets wrong because it reads
 * digits as characters and puts 10 after 1 and before 30 only by accident. A label with no
 * number at all sorts last rather than first: it is the odd one out, not the smallest.
 */
function sizeRank(row: AnyStockPageRow): number {
  const match = /(\d+(?:\.\d+)?)/.exec(row.size_label ?? '');
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

/**
 * How two sizes order against each other under the active sort. One comparator, used by both
 * modes, which is what makes "sorted the same either way" a fact rather than a hope.
 *
 * Sorting by product means the product name A to Z with sizes ascending inside it. Every
 * other column sorts by its own rank and falls back to that same name-then-size order, so
 * two rows with identical numbers do not shuffle between renders.
 */
export function compareRows<R extends AnyStockPageRow>(a: R, b: R, sort: DataTableSort): number {
  const byName = a.name.localeCompare(b.name) || sizeRank(a) - sizeRank(b);
  if (sort.by === 'product') return (sort.dir === 'asc' ? 1 : -1) * byName;
  return compare(rowRank(a, sort.by), rowRank(b, sort.by), sort.dir) || byName;
}

/** Families and their sizes, both ordered by the sorted column. */
export function sortFamilies<F extends AnyStockPageFamily>(
  families: readonly F[],
  sort: DataTableSort,
): F[] {
  const byName = sort.by === 'product';
  const ordered = families.map((family) => ({
    ...family,
    rows: [...family.rows].sort((a, b) => compareRows(a, b, sort)),
  }));

  ordered.sort((a, b) =>
    byName
      ? (sort.dir === 'asc' ? 1 : -1) * a.label.localeCompare(b.label)
      : compare(familyRank(a, sort.by), familyRank(b, sort.by), sort.dir) ||
        a.label.localeCompare(b.label),
  );
  return ordered;
}

/**
 * Every size from every family, in one list, under the active sort.
 *
 * The family ordering is deliberately not consulted: flat mode has no families, so a row's
 * position is decided by the row alone. That is the difference between the two modes, and it
 * is the whole difference.
 */
export function flattenRows<F extends AnyStockPageFamily>(
  families: readonly F[],
  sort: DataTableSort,
): F['rows'][number][] {
  return families
    .flatMap((family) => family.rows as F['rows'][number][])
    .sort((a, b) => compareRows(a, b, sort));
}

export function StockTable({
  families,
  grouping,
  config,
  legend,
  sort,
  onSortChange,
  canEditQty,
  canEditPrice,
  canSetVisible,
  onCommitQty,
  onCommitPrice,
  onSetVisible,
  onOpenDetail,
  onOpenWac,
  loading,
}: StockTableProps) {
  const [collapsed, setCollapsed] = useLocalPreference<string[]>(COLLAPSED_FAMILIES_KEY, []);
  const collapsedSet = useMemo(() => new Set(collapsed), [collapsed]);

  // One scale for every bar on the page, so a bar in GLP means the same as a bar in GLOW.
  const peakUnits = useMemo(
    () =>
      families.reduce(
        (peak, family) => family.rows.reduce((inner, row) => Math.max(inner, row.units_30d), peak),
        0,
      ),
    [families],
  );

  const hint = (id: string) => definitionFor(legend, COLUMN_LEGEND_ID[id]);

  /**
   * Whether the rows carry money at all. A packer's do not (CONTRACTS.md 3.6), and for her
   * the four money columns are not rendered: not blank, not dashed, not there. The per-cell
   * guards below are the type system's share of the same rule.
   */
  const showMoney = useMemo(() => pageHasMoney(families), [families]);

  const columns = useMemo<Column<AnyStockPageRow>[]>(() => {
    const all: Column<AnyStockPageRow>[] = [
      {
        id: 'product',
        header: 'Product / size',
        priority: 1,
        width: '260px',
        sortable: true,
        hint: hint('product'),
        render: (row) => <ProductCell row={row} band={FAMILY_BAND[row.family]} />,
      },
      {
        id: 'on_hand',
        header: 'In stock',
        priority: 1,
        align: 'right',
        width: '132px',
        sortable: true,
        hint: hint('on_hand'),
        render: (row) => <QtyCell row={row} canEdit={canEditQty} onCommit={onCommitQty} />,
      },
      {
        id: 'sales_30d',
        header: '30-day sales',
        priority: 1,
        width: '196px',
        sortable: true,
        hint: hint('sales_30d'),
        render: (row) => (
          <SalesCell
            row={row}
            peakUnits={peakUnits}
            criticalDays={config.critical_days}
            lowDays={config.low_days}
          />
        ),
      },
      {
        id: 'price',
        header: 'Sale price',
        priority: 2,
        align: 'right',
        width: '112px',
        money: true,
        sortable: true,
        hint: hint('price'),
        render: (row) =>
          rowHasMoney(row) ? (
            <PriceCell row={row} canEdit={canEditPrice} onCommit={onCommitPrice} />
          ) : null,
      },
      {
        id: 'inv_value',
        header: 'Inv value',
        priority: 3,
        align: 'right',
        width: '112px',
        money: true,
        sortable: true,
        hint: hint('inv_value'),
        render: (row) => (rowHasMoney(row) ? <MoneyText cents={row.inv_value_cents} /> : null),
      },
      {
        id: 'cost',
        header: 'Cost (WAC)',
        priority: 4,
        align: 'right',
        width: '156px',
        money: true,
        sortable: true,
        hint: hint('cost'),
        render: (row) => (rowHasMoney(row) ? <CostCell row={row} onOpenBreakdown={onOpenWac} /> : null),
      },
      {
        id: 'best_price',
        header: 'Best price',
        priority: 5,
        width: '156px',
        money: true,
        sortable: true,
        hint: hint('best_price'),
        render: (row) =>
          rowHasMoney(row) ? (
            <BestPriceCell row={row} onOpenQuotes={(target) => onOpenDetail(target, 'quotes')} />
          ) : null,
      },
      {
        id: 'actions',
        header: 'Actions',
        priority: 2,
        width: '128px',
        hint: hint('actions'),
        // A control the operator cannot use is not rendered disabled, it is not rendered.
        // The packer holds no stock.set_visible, so her Actions cell is the Detail button.
        render: (row) => (
          <span className="stock-actions">
            <Button size="sm" variant="ghost" onClick={() => onOpenDetail(row)}>
              Detail
            </Button>
            {canSetVisible ? (
              <label className="stock-hide">
                <input
                  type="checkbox"
                  checked={!row.is_visible}
                  onChange={(event) => onSetVisible(row, !event.target.checked)}
                />
                <span className="o-visually-hidden">{`Hide ${row.sku ?? row.name}`}</span>
                <span aria-hidden="true" className="o-muted">
                  Hide
                </span>
              </label>
            ) : null}
          </span>
        ),
      },
    ];
    return all.filter((column) => showMoney || !column.money);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      showMoney,
      canEditPrice,
      canEditQty,
      canSetVisible,
      config.critical_days,
      config.low_days,
      legend,
      onCommitPrice,
      onCommitQty,
      onOpenDetail,
      onOpenWac,
      onSetVisible,
      peakUnits,
    ],
  );

  const ordered = useMemo(() => sortFamilies(families, sort), [families, sort]);
  const flat = useMemo(
    () => (grouping === 'flat' ? flattenRows(families, sort) : []),
    [families, grouping, sort],
  );

  if (!loading && ordered.length === 0) {
    return <EmptyState message="No SKUs match these filters." />;
  }

  const toggle = (key: string) => {
    setCollapsed(
      collapsedSet.has(key) ? collapsed.filter((entry) => entry !== key) : [...collapsed, key],
    );
  };

  if (grouping === 'flat') {
    // No headers, no subtotals, no collapse. The colour band stays on the product cell, so a
    // row still says which family it belongs to without a header to say it for them.
    return (
      <div className="stock-groups">
        <DataTable
          columns={columns}
          rows={flat}
          rowKey={(row) => String(row.product_id)}
          emptyState={<EmptyState message="No SKUs match these filters." />}
          caption="Stock, all sizes"
          loading={loading}
          sort={sort}
          onSortChange={onSortChange}
        />
      </div>
    );
  }

  return (
    <div className="stock-groups">
      {ordered.map((family) => {
        const isCollapsed = collapsedSet.has(family.key);
        return (
          <section key={family.key} className="stock-group">
            <button
              type="button"
              className="stock-group__head"
              aria-expanded={!isCollapsed}
              onClick={() => toggle(family.key)}
            >
              <span
                className="stock-group__band"
                style={{ background: FAMILY_BAND[family.key] }}
                aria-hidden="true"
              />
              <ChevronIcon turn={isCollapsed ? 0 : 90} />
              <span className="stock-group__label">{family.label}</span>
              <span className="stock-group__totals">
                <span className="o-num">{formatCount(family.on_hand)}</span>
                <span className="o-muted">on hand</span>
              </span>
              <span className="stock-group__totals">
                <span className="o-num">{formatCount(family.units_30d)}</span>
                <span className="o-muted">30d units</span>
              </span>
              {familyHasMoney(family) ? (
                <>
                  <span className="stock-group__totals">
                    <MoneyText cents={family.revenue_30d_cents} />
                    <span className="o-muted">30d revenue</span>
                  </span>
                  <span className="stock-group__totals">
                    <MoneyText cents={family.inv_value_cents} />
                    <span className="o-muted">inv value</span>
                  </span>
                </>
              ) : null}
            </button>
            {isCollapsed ? null : (
              <DataTable
                columns={columns}
                rows={family.rows}
                rowKey={(row) => String(row.product_id)}
                emptyState={<EmptyState message="No sizes in this family." />}
                caption={`Stock, ${family.label}`}
                loading={loading}
                sort={sort}
                onSortChange={onSortChange}
              />
            )}
          </section>
        );
      })}
    </div>
  );
}

/** The hidden SKUs at the foot of the page. Out of every KPI, listed so they are not lost. */
export interface HiddenPanelProps {
  rows: readonly AnyStockPageRow[];
  legend: LegendIndex;
  canSetVisible: boolean;
  onUnhide: (row: AnyStockPageRow) => void;
  open: boolean;
  onToggle: () => void;
}

export function HiddenPanel({
  rows,
  legend,
  canSetVisible,
  onUnhide,
  open,
  onToggle,
}: HiddenPanelProps) {
  if (rows.length === 0) return null;
  return (
    <section className="stock-hidden">
      <DefinitionPopover definition={definitionFor(legend, 'stock.hidden')} id="stock-hidden-panel">
        <button
          type="button"
          className="stock-hidden__head"
          aria-expanded={open}
          onClick={onToggle}
        >
          <ChevronIcon turn={open ? 90 : 0} />
          <span>{`Hidden SKUs (${rows.length})`}</span>
        </button>
      </DefinitionPopover>
      {open ? (
        <ul className="stock-hidden__list" role="list">
          {rows.map((row) => (
            <li key={row.product_id} className="stock-hidden__item">
              <span className="stock-name">
                {row.name}
                {row.size_label ? <span className="o-muted"> {row.size_label}</span> : null}
              </span>
              <span className="o-mono o-muted">{row.sku ?? 'no SKU'}</span>
              {canSetVisible ? (
                <Button size="sm" variant="ghost" onClick={() => onUnhide(row)}>
                  Unhide
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
